import { c as _c } from "react/compiler-runtime";
import { DropdownItem } from '@precis-digital/kurama/Dropdown';
import FilterIcon from '@precis-digital/kurama/icons/FilterIcon';
import MultiSelectDropdown, { type MultiSelectDropdownItem } from '@precis-digital/kurama/MultiSelectDropdown';
import Tooltip from '@precis-digital/kurama/Tooltip';
import { type ReactElement } from 'react';
import type { BaseElementDescription, ChartCardTFunction, ChartDataRow } from '../../../types';
import { assertNever, defaultTFunction, getDimensionLabel, getLabelOrValueAsLabel } from '../../../utils/generics';
import { getFilterValueForColumnId, isColumnWithAccessorKey } from '../utils';
import type { ColumnElementDescription } from '../types';
import { TableColumnValuesChoice } from '../../../constants';
import { NULL_FILTER_VALUE } from '../utils/filterAndSortingFns';
import type { ChartCardTableHeaderContextResolved } from '../tanstack';
interface TableHeadCellFilteringMenuProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> extends ChartCardTableHeaderContextResolved<DataKey, DataRow> {
  handleClose: () => void;
}
const getOptions = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(headerContext: ChartCardTableHeaderContextResolved<DataKey, DataRow>): MultiSelectDropdownItem[] => {
  const {
    meta: contextMeta,
    column
  } = headerContext;
  const elementsDescriptionsMap = contextMeta.chartCard.elementsDescriptionsMap;
  let descriptions: Record<string, BaseElementDescription> | undefined;
  if (isColumnWithAccessorKey(column.columnDef)) {
    const elementDescription = elementsDescriptionsMap[column.columnDef.accessorKey as DataKey] as ColumnElementDescription;
    if (elementDescription != null) {
      const {
        type
      } = elementDescription;
      switch (type) {
        case 'date':
          break;
        case 'string':
        case 'variant':
          descriptions = elementDescription.descriptions;
          break;
        case 'number':
        case 'currency':
        case 'percentage':
          return [];
        default:
          assertNever(type);
      }
    }
  }
  const dataKey = (isColumnWithAccessorKey(column.columnDef) ? column.columnDef.accessorKey : column.id) as DataKey;
  const elementDescription = elementsDescriptionsMap[dataKey];
  return Array.from(column.getFacetedUniqueValues().keys()).map<MultiSelectDropdownItem>(value => ({
    value: value == null ? NULL_FILTER_VALUE : String(value),
    label: elementDescription == null ? getLabelOrValueAsLabel(descriptions?.[String(value)]?.label, value) : getDimensionLabel(elementDescription, value, dataKey)
  }));
};
const TableHeadCellFilteringMenu = t0 => {
  const $ = _c(26);
  let T0;
  let filterValue;
  let handleChange;
  let handleCloseParent;
  let noFiltering;
  let options;
  let t;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    const {
      handleClose: t5,
      ...headerContext
    } = t0;
    handleCloseParent = t5;
    const {
      meta: t6,
      column
    } = headerContext;
    const {
      chartCard: t7
    } = t6;
    const {
      t: t8,
      columnFilters,
      dispatch
    } = t7;
    t = t8 === undefined ? defaultTFunction : t8;
    const {
      columnDef: t9
    } = column;
    const {
      meta: columnMeta
    } = t9;
    options = getOptions(headerContext);
    filterValue = getFilterValueForColumnId(columnFilters, column.id);
    noFiltering = columnMeta?.chartCard?.noFiltering ?? false;
    handleChange = (optionValue, value) => {
      const items = value.map(_temp);
      column.setFilterValue(items);
      dispatch({
        id: `${TableColumnValuesChoice}.${column.id}` as DataKey,
        type: "multipleSet",
        items,
        others: false
      });
    };
    T0 = MultiSelectDropdown;
    t1 = "filter_menu_dropdown";
    t2 = true;
    t3 = t("filterData");
    t4 = t("filterData");
    $[0] = t0;
    $[1] = T0;
    $[2] = filterValue;
    $[3] = handleChange;
    $[4] = handleCloseParent;
    $[5] = noFiltering;
    $[6] = options;
    $[7] = t;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    T0 = $[1];
    filterValue = $[2];
    handleChange = $[3];
    handleCloseParent = $[4];
    noFiltering = $[5];
    options = $[6];
    t = $[7];
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
  }
  const t5 = filterValue as string[];
  let t6;
  if ($[12] !== noFiltering || $[13] !== t) {
    t6 = {
      noFiltering,
      t
    };
    $[12] = noFiltering;
    $[13] = t;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== T0 || $[16] !== handleChange || $[17] !== handleCloseParent || $[18] !== options || $[19] !== t1 || $[20] !== t2 || $[21] !== t3 || $[22] !== t4 || $[23] !== t5 || $[24] !== t6) {
    t7 = <T0 key={t1} includeSearch={t2} title={t3} buttonTitle={t4} options={options} allowSelectAll={true} value={t5} onChange={handleChange} onClose={handleCloseParent} CustomAnchorElement={FilterDropdownItemElement} customAnchorElementProps={t6} fixedPositionOnOpen={true} />;
    $[15] = T0;
    $[16] = handleChange;
    $[17] = handleCloseParent;
    $[18] = options;
    $[19] = t1;
    $[20] = t2;
    $[21] = t3;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  return t7;
};
interface FilterDropdownItemElementProps {
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  isDropdownOpen: boolean;
  noFiltering: boolean;
  t: ChartCardTFunction;
}
const FilterDropdownItemElement = t0 => {
  const $ = _c(4);
  let dropdownItemElement;
  let t1;
  if ($[0] !== t0) {
    t1 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const {
        onClick,
        isDropdownOpen,
        ...propsWithCustomProps
      } = t0;
      const {
        noFiltering,
        t
      } = propsWithCustomProps as FilterDropdownItemElementProps;
      let t2;
      if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = <FilterIcon />;
        $[3] = t2;
      } else {
        t2 = $[3];
      }
      dropdownItemElement = <DropdownItem key="filter_menu" disabled={noFiltering || isDropdownOpen} leftIcon={t2} onClick={onClick}>{t("filterData")}</DropdownItem>;
      if (noFiltering) {
        t1 = <Tooltip key="filter_menu_tooltip" kind="singleline" title={t("noDataFiltering")}>{dropdownItemElement}</Tooltip>;
        break bb0;
      }
    }
    $[0] = t0;
    $[1] = dropdownItemElement;
    $[2] = t1;
  } else {
    dropdownItemElement = $[1];
    t1 = $[2];
  }
  if (t1 !== Symbol.for("react.early_return_sentinel")) {
    return t1;
  }
  return dropdownItemElement;
};
export default TableHeadCellFilteringMenu;
function _temp(item) {
  return typeof item === "string" ? item : item.value;
}